import axios from "axios";
import * as actionTypes from "../action";
import { toast as message } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const actionGetSubscriptionPlans = () => async (dispatch, getState) => {
    dispatch({ type: actionTypes.GET_SUBSCRIPTION_PLANS_LOADER, payload: true });
    try {
        let res = await axios.get(`${BASE_URL}/subscription/plans`);
        dispatch({
            type: actionTypes.GET_SUBSCRIPTION_PLANS_LOADER,
            payload: false,
        });

        if (
            parseInt(res.data.http_status_code) === 200 &&
            res.data.result.status === "OK"
        ) {
            dispatch({
                type: actionTypes.GET_SUBSCRIPTION_PLANS_DATA,
                payload: res.data.result.data,
            });
        } else {
            message.error(res.data.error.error_message || "Failed to fetch plans");
        }
    } catch (error) {
        if (
            error.response &&
            error.response.data &&
            error.response.data.message
        ) {
            message.error(error.response.data.message);
        } else {
            message.error("An error occurred while fetching subscription plans");
        }
        dispatch({
            type: actionTypes.GET_SUBSCRIPTION_PLANS_LOADER,
            payload: false,
        });
    }
};

export const actionSelectSubscriptionPlan =
    (planCode, navigate) => async (dispatch, getState) => {
        try {
            // Redirect to checkout or payment page
            navigate(`/subscription/checkout/${planCode}`);
            message.success("Proceeding to checkout");
        } catch (error) {
            message.error("Failed to process plan selection");
        }
    };

export const actionCreateStripeCustomer = (customerData) => async (dispatch) => {
    dispatch({ type: "SUBSCRIPTION_CUSTOMER_LOADER", payload: true });
    try {
        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/create-customer`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
                body: JSON.stringify(customerData),
            }
        );
        const data = await response.json();

        if (data.result?.status === "OK") {
            dispatch({
                type: "SET_STRIPE_CUSTOMER",
                payload: data.result.data.customer,
            });
            return data.result.data.customer;
        } else {
            throw new Error(data.error?.error_message || "Failed to create customer");
        }
    } catch (error) {
        dispatch({
            type: "SUBSCRIPTION_ERROR",
            payload: error.message,
        });
        throw error;
    } finally {
        dispatch({ type: "SUBSCRIPTION_CUSTOMER_LOADER", payload: false });
    }
};

export const actionCreateSubscription = (subscriptionData) => async (dispatch) => {
    dispatch({ type: "SUBSCRIPTION_CREATE_LOADER", payload: true });
    try {
        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/create-subscription`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
                body: JSON.stringify(subscriptionData),
            }
        );
        const data = await response.json();

        if (data.result?.status === "OK") {
            dispatch({
                type: "SET_ACTIVE_SUBSCRIPTION",
                payload: data.result.data,
            });
            // Return the data object which contains subscriptionId and clientSecret
            return data.result.data;
        } else {
            throw new Error(data.error?.error_message || "Failed to create subscription");
        }
    } catch (error) {
        dispatch({
            type: "SUBSCRIPTION_ERROR",
            payload: error.message,
        });
        throw error;
    } finally {
        dispatch({ type: "SUBSCRIPTION_CREATE_LOADER", payload: false });
    }
};

export const actionCancelSubscription = (subscriptionId, cancelAtPeriodEnd = true) => async (dispatch) => {
    dispatch({ type: "SUBSCRIPTION_CANCEL_LOADER", payload: true });

    try {
        console.log("Canceling subscription:", subscriptionId, "cancelAtPeriodEnd:", cancelAtPeriodEnd);

        if (!subscriptionId) {
            throw new Error("Subscription ID is required");
        }

        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/${subscriptionId}/cancel`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "accept": "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
                body: JSON.stringify({
                    cancelAtPeriodEnd: cancelAtPeriodEnd,
                }),
            }
        );

        const data = await response.json();
        console.log("Cancel subscription response:", data);

        if (parseInt(data.http_status_code) === 200 && data.result?.status === "OK") {
            dispatch({
                type: "SUBSCRIPTION_CANCEL_SUCCESS",
                payload: data.result.data,
            });
            message.success(cancelAtPeriodEnd
                ? "Subscription will be canceled at the end of current billing period"
                : "Subscription canceled successfully"
            );
            return data.result.data;
        } else {
            throw new Error(data.error?.error_message || "Failed to cancel subscription");
        }
    } catch (error) {
        console.error("Cancel subscription error:", error);
        dispatch({
            type: "SUBSCRIPTION_ERROR",
            payload: error.message,
        });
        message.error(error.message || "Failed to cancel subscription");
        throw error;
    } finally {
        dispatch({ type: "SUBSCRIPTION_CANCEL_LOADER", payload: false });
    }
};

export const actionReactivateSubscription = (subscriptionId) => async (dispatch) => {
    dispatch({ type: "SUBSCRIPTION_REACTIVATE_LOADER", payload: true });

    try {
        console.log("Reactivating subscription:", subscriptionId);

        if (!subscriptionId) {
            throw new Error("Subscription ID is required");
        }

        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/${subscriptionId}/reactivate`,
            {
                method: "POST",
                headers: {
                    "accept": "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
            }
        );

        const data = await response.json();
        console.log("Reactivate subscription response:", data);

        if (parseInt(data.http_status_code) === 200 && data.result?.status === "OK") {
            dispatch({
                type: "SUBSCRIPTION_REACTIVATE_SUCCESS",
                payload: data.result.data,
            });
            message.success("Subscription reactivated successfully!");
            return data.result.data;
        } else {
            throw new Error(data.error?.error_message || "Failed to reactivate subscription");
        }
    } catch (error) {
        console.error("Reactivate subscription error:", error);
        dispatch({
            type: "SUBSCRIPTION_ERROR",
            payload: error.message,
        });
        message.error(error.message || "Failed to reactivate subscription");
        throw error;
    } finally {
        dispatch({ type: "SUBSCRIPTION_REACTIVATE_LOADER", payload: false });
    }
};

export const actionGetPaymentMethods = (customerId) => async (dispatch) => {
    dispatch({ type: "PAYMENT_METHODS_LOADER", payload: true });

    try {
        console.log("Getting payment methods for customer:", customerId);

        if (!customerId) {
            throw new Error("Customer ID is required");
        }

        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/customers/${customerId}/payment-methods`,
            {
                method: "GET",
                headers: {
                    "accept": "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
            }
        );

        const data = await response.json();
        console.log("Payment methods response:", data);

        if (parseInt(data.http_status_code) === 200 && data.result?.status === "OK") {
            // Extract payment methods from the correct path
            const paymentMethods = data.result.data.paymentMethods || [];
            dispatch({
                type: "PAYMENT_METHODS_SUCCESS",
                payload: paymentMethods,
            });
            return paymentMethods;
        } else {
            throw new Error(data.error?.error_message || "Failed to fetch payment methods");
        }
    } catch (error) {
        console.error("Get payment methods error:", error);
        dispatch({
            type: "SUBSCRIPTION_ERROR",
            payload: error.message,
        });
        message.error(error.message || "Failed to fetch payment methods");
        throw error;
    } finally {
        dispatch({ type: "PAYMENT_METHODS_LOADER", payload: false });
    }
};

export const actionSetDefaultPaymentMethod = (customerId, paymentMethodId) => async (dispatch) => {
    dispatch({ type: "SET_DEFAULT_PAYMENT_METHOD_LOADER", payload: true });

    try {
        console.log("Setting default payment method:", { customerId, paymentMethodId });

        if (!customerId || !paymentMethodId) {
            throw new Error("Customer ID and Payment Method ID are required");
        }

        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/customers/${customerId}/default-payment-method`,
            {
                method: "POST",
                headers: {
                    "accept": "application/json",
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
                body: JSON.stringify({
                    paymentMethodId: paymentMethodId,
                }),
            }
        );

        const data = await response.json();
        console.log("Set default payment method response:", data);

        if (parseInt(data.http_status_code) === 200 && data.result?.status === "OK") {
            dispatch({
                type: "SET_DEFAULT_PAYMENT_METHOD_SUCCESS",
                payload: data.result.data,
            });
            message.success("Default payment method updated successfully!");
            return data.result.data;
        } else {
            throw new Error(data.error?.error_message || "Failed to set default payment method");
        }
    } catch (error) {
        console.error("Set default payment method error:", error);
        dispatch({
            type: "SUBSCRIPTION_ERROR",
            payload: error.message,
        });
        message.error(error.message || "Failed to set default payment method");
        throw error;
    } finally {
        dispatch({ type: "SET_DEFAULT_PAYMENT_METHOD_LOADER", payload: false });
    }
};

export const actionAddPaymentMethod = (customerId, paymentMethodId) => async (dispatch) => {
    dispatch({ type: "ADD_PAYMENT_METHOD_LOADER", payload: true });
    try {
        if (!customerId || !paymentMethodId) {
            throw new Error("Customer ID and Payment Method ID are required");
        }

        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/customers/${customerId}/payment-methods`,
            {
                method: "POST",
                headers: {
                    accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
                body: JSON.stringify({ paymentMethodId }),
            }
        );

        const data = await response.json();

        if (parseInt(data.http_status_code) === 200 && data.result?.status === "OK") {
            message.success("Payment method added successfully!");
            return data.result.data;
        }
        throw new Error(data.error?.error_message || "Failed to add payment method");
    } catch (error) {
        dispatch({ type: "SUBSCRIPTION_ERROR", payload: error.message });
        message.error(error.message || "Failed to add payment method");
        throw error;
    } finally {
        dispatch({ type: "ADD_PAYMENT_METHOD_LOADER", payload: false });
    }
};

export const actionRemovePaymentMethod = (customerId, paymentMethodId) => async (dispatch) => {
    dispatch({ type: "REMOVE_PAYMENT_METHOD_LOADER", payload: true });
    try {
        if (!customerId || !paymentMethodId) {
            throw new Error("Customer ID and Payment Method ID are required");
        }

        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/subscription/customers/${customerId}/payment-methods/${paymentMethodId}`,
            {
                method: "DELETE",
                headers: {
                    accept: "application/json",
                    Authorization: localStorage.getItem("authToken"),
                },
            }
        );

        const data = await response.json();

        if (parseInt(data.http_status_code) === 200 && data.result?.status === "OK") {
            message.success("Payment method removed.");
            return data.result.data;
        }
        throw new Error(data.error?.error_message || "Failed to remove payment method");
    } catch (error) {
        dispatch({ type: "SUBSCRIPTION_ERROR", payload: error.message });
        message.error(error.message || "Failed to remove payment method");
        throw error;
    } finally {
        dispatch({ type: "REMOVE_PAYMENT_METHOD_LOADER", payload: false });
    }
};

export const actionGetBillingHistory = (page = 1, limit = 20) => async (dispatch) => {
    dispatch({ type: "BILLING_HISTORY_LOADER", payload: true });
    try {
        const res = await axios.get(
            `${BASE_URL}/subscription/billing-history?page=${page}&limit=${limit}`,
            { headers: { Authorization: localStorage.getItem("authToken") } }
        );
        const data = res.data;
        // generateResponse shape: { result: { status, data: { status, total, page, totalPages, data: rows } } }
        if (data.result?.status === "OK") {
            dispatch({ type: "SET_BILLING_HISTORY", payload: data.result.data });
            return data.result.data;
        }
        throw new Error(data.error?.error_message || "Failed to fetch billing history");
    } catch (error) {
        dispatch({ type: "SUBSCRIPTION_ERROR", payload: error.message });
        throw error;
    } finally {
        dispatch({ type: "BILLING_HISTORY_LOADER", payload: false });
    }
};

export const actionGetInvoicePdf = (invoiceId) => async () => {
    const res = await axios.get(
        `${BASE_URL}/subscription/billing-history/invoice/${invoiceId}`,
        { headers: { Authorization: localStorage.getItem("authToken") } }
    );
    return res.data?.result?.data?.data ?? null;
};
